import dayjs from 'dayjs';
import { useEffect, useState, type ReactNode } from 'react';
import type { ReportSourceCapture, ReportSourceJournal, ReportSourceLineage } from '@/core/models/reports.models';
import { notifyError } from '@/core/notify/notify';
import { reportsService } from '@/core/services/reports';
import { Button } from '@/kit/Button';
import { Icon } from '@/kit/Icon';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import './ReportSourceLineageDialog.scss';

export interface ReportSourceLineageDialogData {
  reportId: string;
  refNo: string;
  /** The list's Regenerate for this report (it asks first); true once the report was regenerated, so the lineage is read again. */
  onRegenerate?: () => Promise<boolean>;
}

/** The dialog options the list opens it with. */
export const REPORT_SOURCE_LINEAGE_DIALOG = { width: '95vw', maxWidth: '1000px' } as const;

const dateTime = (value: string | null | undefined): string => {
  const date = value ? dayjs(value) : null;
  return date?.isValid() ? date.format('DD-MMM-YYYY HH:mm') : '';
};

const dateOnly = (value: string | null | undefined): string => {
  const date = value ? dayjs(value) : null;
  return date?.isValid() ? date.format('DD-MMM-YYYY') : '';
};

const twoDecimals = (value: number): string =>
  value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** One link of the chain Source TB -> Nominal Mapping -> Adjustments -> Adjusted TB -> Report. */
function ChainStep({ title, children, muted }: { title: string; children: ReactNode; muted?: boolean }) {
  return (
    <li className={`lineage-step${muted ? ' muted' : ''}`}>
      <span className="lineage-step-title">{title}</span>
      {children}
    </li>
  );
}

function JournalsTable({ journals, empty }: { journals: ReportSourceJournal[]; empty: string }) {
  if (journals.length === 0) {
    return <p className="lineage-empty">{empty}</p>;
  }
  return (
    <div className="mat-table-wrapper">
      <table className="w-100 lineage-table">
        <thead>
          <tr>
            <th>No.</th>
            <th>Date</th>
            <th>Type</th>
            <th>Narrative</th>
            <th>Status</th>
            <th className="text-right">Debit</th>
            <th className="text-right">Credit</th>
            <th>Posted</th>
          </tr>
        </thead>
        <tbody>
          {journals.map((journal) => (
            <tr key={journal.journalId}>
              <td>{journal.journalNo}</td>
              <td className="white-space-nowrap">{dateOnly(journal.journalDate)}</td>
              <td>{journal.typeCode}</td>
              <td>{journal.narrative}</td>
              <td>{journal.statusLabel}</td>
              <td className="text-right">{twoDecimals(journal.totalDebit)}</td>
              <td className="text-right">{twoDecimals(journal.totalCredit)}</td>
              <td className="white-space-nowrap">{dateTime(journal.postedAtUtc)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The chain for what one generation of the report was made from: the capture shown, or - before captures were kept - the report itself. */
function Chain({ lineage, shown }: { lineage: ReportSourceLineage; shown: ReportSourceCapture | null }) {
  const adjusted = (shown?.balanceSource ?? lineage.balanceSource) === 1;
  const periodStart = shown?.periodStart ?? lineage.periodStart;
  const periodEnd = shown?.periodEnd ?? lineage.periodEnd;
  return (
    <ol className="lineage-chain" aria-label="Data lineage">
      <ChainStep title="Source TB">
        <strong>{shown?.sourceTrialBalanceRef ?? lineage.currentTrialBalanceRef}</strong>
        {shown?.sourceTrialBalanceUpdatedAtUtc && <span>Last saved {dateTime(shown.sourceTrialBalanceUpdatedAtUtc)}</span>}
      </ChainStep>
      <ChainStep title="Nominal mapping" muted={!adjusted}>
        {adjusted ? (
          <>
            <strong>{plural(shown?.mappedAccountCount ?? 0, 'account mapped', 'accounts mapped')}</strong>
            <span>
              {shown?.mappingChangedAtUtc ? `Last changed ${dateTime(shown.mappingChangedAtUtc)}` : 'No mapping change recorded'}
            </span>
          </>
        ) : (
          <strong>Not used</strong>
        )}
      </ChainStep>
      <ChainStep title="Adjustments" muted={!adjusted}>
        {adjusted ? (
          <>
            <strong>{plural(shown?.journalCount ?? 0, 'journal', 'journals')}</strong>
            <span>Posted, reversed and reversal journals</span>
          </>
        ) : (
          <strong>None</strong>
        )}
      </ChainStep>
      <ChainStep title="Adjusted TB" muted={!adjusted}>
        {adjusted ? (
          <>
            <strong>{shown?.balanceVersion != null ? `Version ${shown.balanceVersion}` : 'Version not recorded'}</strong>
            <span>
              {dateOnly(periodStart)} to {dateOnly(periodEnd)}
            </span>
          </>
        ) : (
          <strong>Not used</strong>
        )}
      </ChainStep>
      <ChainStep title="Report">
        <strong>{lineage.refNo}</strong>
        {shown && (
          <span>
            {shown.reasonLabel} {dateTime(shown.capturedAtUtc)}
          </span>
        )}
      </ChainStep>
    </ol>
  );
}

/**
 * APT342: "Source and lineage" - where a report's figures came from and what went into them: the chain Source TB, nominal mapping,
 * adjustment journals, Adjusted TB (its version) and the report; the journals of the generation shown; every earlier generation
 * (the audit trail, opened by clicking a row); and, once journals were posted after the report, the warning with the journals and
 * the list's Regenerate.
 */
export function ReportSourceLineageDialog({ data }: DialogComponentProps<ReportSourceLineageDialogData, never>) {
  const [lineage, setLineage] = useState<ReportSourceLineage | null>(null);
  const [failed, setFailed] = useState(false);
  const [selectedNo, setSelectedNo] = useState<number | null>(null);
  // Bumped after a regeneration so the lineage is read again.
  const [version, setVersion] = useState(0);
  const reportId = data?.reportId;

  useEffect(() => {
    if (!reportId) {
      return;
    }
    let cancelled = false;
    reportsService.getReportSource(reportId).then(
      (loaded) => {
        if (!cancelled) {
          setLineage(loaded);
          setFailed(false);
          setSelectedNo(null);
        }
      },
      (error) => {
        if (!cancelled) {
          setFailed(true);
          notifyError(error);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [reportId, version]);

  const regenerate = async () => {
    if (await data?.onRegenerate?.()) {
      setVersion((v) => v + 1);
    }
  };

  const shown: ReportSourceCapture | null = lineage
    ? (lineage.history.find((c) => c.captureNo === selectedNo) ?? lineage.latest)
    : null;
  const isLatest = !!shown && shown.captureId === lineage?.latest?.captureId;

  return (
    <div className="mat-dialog-wrapper report-source-lineage">
      <DialogContent>
        <div className="main-contain">
          <div className="sidenav-header">
            <div className="d-flex align-items-center justify-space-between">
              <h2>
                <span>Source and lineage - {data?.refNo}</span>
              </h2>
              <DialogClose>
                <Icon name="close" className="close" />
              </DialogClose>
            </div>
          </div>
        </div>

        {failed && <p className="lineage-empty">The source of this report could not be read.</p>}
        {!lineage && !failed && <p className="lineage-empty">Loading...</p>}

        {lineage && (
          <>
            <p className="lineage-source fw-bold">{lineage.sourceLabel}</p>

            {lineage.sourceChanged && (
              <div className="lineage-warning" role="alert">
                <Icon name="update" outlined className="font-size-18" />
                <span className="lineage-warning-text">
                  {lineage.sourceChangedReason ??
                    'The adjusted balances changed after this report was generated - regenerate to use the latest figures.'}
                </span>
                {data?.onRegenerate && (
                  <Button className="action-button primary-button" onClick={() => void regenerate()}>
                    <Icon name="sync" outlined />
                    Regenerate
                  </Button>
                )}
              </div>
            )}

            <Chain lineage={lineage} shown={shown} />

            {lineage.balanceSource === 1 && lineage.currentBalanceVersion != null && (
              <p className="lineage-current">
                The Adjusted Trial Balance is at version {lineage.currentBalanceVersion}
                {lineage.currentBalancesAsOfUtc ? ` (rebuilt ${dateTime(lineage.currentBalancesAsOfUtc)})` : ''}
                {shown?.balanceVersion != null ? `; this report was generated from version ${shown.balanceVersion}.` : '.'}
              </p>
            )}
            {lineage.note && <p className="lineage-note">{lineage.note}</p>}

            {lineage.journalsSince.length > 0 && (
              <section>
                <h3>Journals posted since this report was generated</h3>
                <JournalsTable journals={lineage.journalsSince} empty="" />
              </section>
            )}

            {shown && (
              <section>
                <h3>
                  Adjustment journals in {isLatest ? 'the latest generation' : `generation ${shown.captureNo}`} (
                  {dateTime(shown.capturedAtUtc)})
                </h3>
                <JournalsTable
                  journals={shown.journals}
                  empty={
                    shown.balanceSource === 1
                      ? 'No adjustment journals were in the Adjusted Trial Balance.'
                      : 'A trial balance as saved has no adjustment journals.'
                  }
                />
              </section>
            )}

            <section>
              <h3>History</h3>
              {lineage.history.length === 0 ? (
                <p className="lineage-empty">No generation of this report was recorded yet.</p>
              ) : (
                <div className="mat-table-wrapper">
                  <table className="w-100 lineage-table lineage-history">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>When</th>
                        <th>What happened</th>
                        <th>Trial balance</th>
                        <th>Version</th>
                        <th>Journals</th>
                        <th>By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineage.history.map((capture) => (
                        <tr
                          key={capture.captureId}
                          className={capture.captureId === shown?.captureId ? 'selected' : undefined}
                          onClick={() => setSelectedNo(capture.captureNo)}
                        >
                          <td>{capture.captureNo}</td>
                          <td className="white-space-nowrap">{dateTime(capture.capturedAtUtc)}</td>
                          <td>{capture.reasonLabel}</td>
                          <td>{capture.sourceTrialBalanceRef}</td>
                          <td>{capture.balanceVersion ?? '-'}</td>
                          <td>{capture.balanceSource === 1 ? capture.journalCount : '-'}</td>
                          <td>{capture.capturedByName ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </DialogContent>
    </div>
  );
}
